import * as THREE from "three";

// exp² fog is 95% opaque where density * distance = sqrt(3).
const OPAQUE_95 = Math.sqrt(3);

export default class Atmosphere {
    constructor({ sky, terrainMaterial, scene, camera, debug, stormFolder }) {
        this.sky = sky;
        this.terrainMaterial = terrainMaterial;
        this.scene = scene;
        this.camera = camera;
        this.params = {
            density: 0.004,
            heightFalloff: 0.018,
            start: 70,
            sunScatter: 0.45,
            sceneDensity: 0.00135,
            stormVisibility: 24,
            stormOrigin: 0.7,
            stormNight: 0.12,
        };
        this.stormColor = new THREE.Color("#d28537");
        this.nightScale = 1;
        this._stormTint = new THREE.Color();
        this._fogColor = new THREE.Color();
        this._fogSRGB = new THREE.Color();
        this.setDebug(debug, stormFolder);
    }

    // focus is the player position.
    update(stormIntensity, focus) {
        const params = this.params;
        const uniforms = this.terrainMaterial.uniforms;
        const night = this.sky.getNight();

        this.nightScale = THREE.MathUtils.lerp(1, params.stormNight, night);
        this._stormTint.copy(this.stormColor).multiplyScalar(this.nightScale);
        this._fogColor
            .copy(this.sky.getHorizonColor())
            .lerp(this._stormTint, stormIntensity);
        this.sky.setStorm(stormIntensity, this._stormTint);

        this._fogSRGB.copy(this._fogColor).convertLinearToSRGB();
        uniforms.uFogColor.value.copy(this._fogSRGB);
        uniforms.uFogDensity.value = params.density;
        uniforms.uFogHeightFalloff.value = params.heightFalloff;
        uniforms.uFogStart.value = params.start;
        uniforms.uSunScatter.value =
            params.sunScatter * (1 - night) * (1 - stormIntensity);
        uniforms.uSunDir.value.copy(this.sky.getSunDir());

        const stormDensity =
            (stormIntensity * OPAQUE_95) / params.stormVisibility;
        uniforms.uStormDensity.value = stormDensity;
        uniforms.uStormOrigin.value.lerpVectors(
            this.camera.position,
            focus,
            params.stormOrigin,
        );

        // FogExp2 is measured from the camera. Scaling the storm term by the
        // origin offset gives the robot the same haze the terrain has under it.
        this.scene.fog.color.copy(this._fogColor);
        this.scene.fog.density = Math.hypot(
            params.sceneDensity,
            stormDensity * (1 - params.stormOrigin),
        );
    }

    setDebug(debug, stormFolder) {
        if (!debug?.active) return;
        const folder = debug.ui.addFolder("Atmosphere");
        folder.add(this.params, "density", 0, 0.012, 0.0001).name("density");
        folder
            .add(this.params, "heightFalloff", 0, 0.08, 0.001)
            .name("height falloff");
        folder.add(this.params, "start", 0, 400, 1).name("start");
        folder.add(this.params, "sunScatter", 0, 1, 0.01).name("sun scatter");

        if (!stormFolder) return;
        const fog = stormFolder.addFolder("Fog");
        fog.add(this.params, "stormVisibility", 2, 200, 0.5).name("visibility");
        debug.addColor(fog, this.stormColor, "color");
        fog.add(this.params, "stormNight", 0, 1, 0.01).name("night brightness");
        fog.add(this.params, "stormOrigin", 0, 1, 0.01).name(
            "centre cam → player",
        );
    }
}
